import { useTranslation } from 'react-i18next'

import {
  Alert,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow
} from '@vtex/shoreline'

import ListPagination from '~/components/ListPagination'
import { LogoStore } from '~/components/LogoStore'

import {
  AdvertiserCell,
  Card,
  CardHeading,
  CardTitle,
  EmptyBox,
  EmptyText,
  PaginationWrap,
  TableWrap
} from '../styles'
import type { DestinationCampaignRow } from '../types'

const COLUMN_WIDTHS = ['minmax(12rem, 1fr)', '10rem']
const ITEMS_PER_PAGE_OPTIONS = [10, 20, 50]

const CampaignsRedirectingHere = ({
  rows,
  total,
  currentPage,
  itemsPerPage,
  onPageChange,
  onItemsPerPageChange,
  isLoading = false,
  error
}: {
  rows: DestinationCampaignRow[]
  total: number
  currentPage: number
  itemsPerPage: number
  onPageChange: (page: number) => void
  onItemsPerPageChange: (quantity: number) => void
  isLoading?: boolean
  error: boolean
}) => {
  const { t } = useTranslation()

  const renderContent = () => {
    if (error) {
      return (
        <Alert variant="critical">
          {t('common:messages.errorOccurred.message')}
        </Alert>
      )
    }

    if (isLoading) {
      return (
        <EmptyBox>
          <EmptyText>{t('c:words.loading')}</EmptyText>
        </EmptyBox>
      )
    }

    if (rows.length === 0) {
      return (
        <EmptyBox>
          <EmptyText>
            {t('rm:config.destinationRetail.campaigns.empty')}
          </EmptyText>
        </EmptyBox>
      )
    }

    return (
      <TableWrap>
        <Table columnWidths={COLUMN_WIDTHS}>
          <TableHeader>
            <TableRow>
              <TableHeaderCell>
                {t('rm:config.destinationRetail.campaigns.column.advertiser')}
              </TableHeaderCell>
              <TableHeaderCell>
                {t(
                  'rm:config.destinationRetail.campaigns.column.activeCampaigns'
                )}
              </TableHeaderCell>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map(row => (
              <TableRow key={row.advertiser_id}>
                <TableCell>
                  <AdvertiserCell>
                    <LogoStore
                      alt={row.advertiser_name}
                      linkLogo={row.advertiser_account_logo ?? undefined}
                      size="small"
                    />
                    {row.advertiser_name}
                  </AdvertiserCell>
                </TableCell>
                <TableCell>{row.campaigns_count}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <PaginationWrap>
          <ListPagination
            total={total}
            label={t('rm:config.destinationRetail.campaigns.title')}
            currentPage={currentPage}
            itemsPerPage={itemsPerPage}
            onClickPagination={onPageChange}
            onItemsPerPageChange={({ value }: { value: number }) => {
              onItemsPerPageChange(value)
            }}
            customOptions={ITEMS_PER_PAGE_OPTIONS}
          />
        </PaginationWrap>
      </TableWrap>
    )
  }

  return (
    <Card>
      <CardHeading>
        <CardTitle>
          {t('rm:config.destinationRetail.campaigns.title')}
        </CardTitle>
      </CardHeading>
      {renderContent()}
    </Card>
  )
}

export default CampaignsRedirectingHere
